/**
 * Partners API Client
 * Epic 7: Partner Management
 */

import { z } from 'zod';
import { api } from './client';
// page9 (#420) ContactPerson read-seam — FE-safe dto sub-path (vite/vitest+tsconfig alias, #357 minta)
import {
  ContactPersonListResponseSchema,
  type ContactPersonListResponseDto,
} from '@kgc/partners/contact-person';

// ============================================
// TYPES
// ============================================

export type PartnerType = 'individual' | 'company';
export type PartnerStatus = 'active' | 'inactive' | 'blocked' | 'trusted' | 'warning';
export type PartnerCategory =
  | 'retail'
  | 'wholesale'
  | 'rental'
  | 'service'
  | 'supplier'
  | 'contractor';

export interface PartnerAddress {
  id: string;
  type: 'billing' | 'shipping' | 'other';
  isDefault: boolean;
  country: string;
  postalCode: string;
  city: string;
  street: string;
  building?: string;
}

export interface PartnerContact {
  id: string;
  name: string;
  position?: string;
  email?: string;
  phone?: string;
  isPrimary: boolean;
}

export interface Partner {
  id: string;
  code: string;
  type: PartnerType;
  status: PartnerStatus;
  name: string;
  shortName?: string;
  companyName?: string;
  contactName?: string;
  email?: string;
  phone?: string;
  phoneAlt?: string;
  taxNumber?: string;
  euVatNumber?: string;
  registrationNumber?: string;
  website?: string;
  country?: string;
  postalCode?: string;
  city?: string;
  address?: string;
  addressAlt?: string;
  birthDate?: string | undefined;
  birthPlace?: string;
  /** Anyja neve — #188 óta nyersen tárolt (szerződés-szükséges, 6(1)(b)), a GET visszaadja. */
  mothersName?: string;
  maskedIdCardNumber?: string;
  /** Story 70-1E-a: ID card expiry (ISO date string). Past value → wizard warns. */
  idCardExpiry?: string | undefined;
  /**
   * Story 70-1E-a: ID card type classification from OCR/manual entry.
   * Mirrors @kgc/tesseract-provider IdCardType enum.
   */
  idCardType?:
    | 'HU_ID_CARD'
    | 'HU_PASSPORT'
    | 'HU_DRIVING_LICENSE'
    | 'EU_ID_CARD'
    | 'EU_PASSPORT'
    | 'OTHER'
    | undefined;
  costBearerId?: string;
  costBearerName?: string;
  costBearerTaxNumber?: string;
  // page9 type-fix (nagykör-1, 2026-06-11): a GET-DTO kiadja
  // (partner-direct.controller.ts:141-144 + mapper :1069-1074), a típus eddig nem deklarálta.
  /** Törzsvendég szint (nested { name } — loyaltyTier.tierName mappelve) */
  loyaltyTier?: { name: string } | undefined;
  /** Kaució-mentesség (REQUIRED = kötelező default | EXEMPT = kézi mentesítés | AUTOMATIC = TRUSTED-alapú) */
  depositExemption?: 'REQUIRED' | 'EXEMPT' | 'AUTOMATIC' | undefined;
  // FG-1 contact/address/scan fields — a GET-DTO visszaadja
  // (partner-direct.controller.ts:79-84). A dátumok ISO-string-ek (toISOString).
  notificationEmail?: string;
  billingEmail?: string;
  temporaryAddress?: string;
  tempAddressValidUntil?: string;
  lastDocumentScanDate?: string;
  paymentBypassEnabled?: boolean;
  // #196 (Q-T030) okmány-verify (manuális staff-verify) — GET-DTO :88-90.
  idVerified?: boolean;
  idVerifiedBy?: string;
  idVerifiedAt?: string;
  creditLimit?: number;
  currentBalance: number;
  paymentTermDays: number;
  defaultDiscountPc: number;
  discountPercent?: number;
  discountRental?: number;
  discountSales?: number;
  discountService?: number;
  loyaltyPoints: number;
  priceListId?: string;
  rentalDepositPercent?: number;
  rentalBlocked?: boolean;
  warningNote?: string;
  notes?: string;
  internalNotes?: string;
  // E-90: Segment & Sanction
  segment?: string;
  transferBanLevel?: string;
  transferBanUntil?: string;
  bookingRestricted?: boolean;
  bookingRestrictedUntil?: string;
  bookingWarningFlag?: boolean;
  paymentStrikeCount?: number;
  categories: PartnerCategory[];
  addresses: PartnerAddress[];
  contacts: PartnerContact[];
  phones?: Array<{ id: string; label: string; number: string; isPrimary: boolean }> | undefined;
  stats: {
    totalOrders: number;
    totalRevenue: number;
    totalRentals: number;
    totalServiceOrders: number;
    outstandingBalance: number;
    lastOrderDate?: string;
    lastRentalDate?: string;
  };
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export interface PartnerListResponse {
  data: Partner[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
  };
}

export interface PartnerResponse {
  data: Partner;
}

export interface PartnerStats {
  total: number;
  active: number;
  companies: number;
  withBalance: number;
  totalBalance: number;
}

export interface PartnerFilters {
  type?: 'INDIVIDUAL' | 'COMPANY';
  status?: 'ACTIVE' | 'INACTIVE' | 'BLACKLISTED' | 'TRUSTED' | 'WARNING';
  search?: string;
  page?: number;
  pageSize?: number;
  /**
   * Ha true, a szerver csak a >0 bérléssel rendelkező partnereket adja
   * (renter-default-view, PV-bug-fix). Üres search → true; user-search → undefined.
   */
  hasRentals?: boolean | undefined;
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Get list of partners with filters
 */
export async function getPartners(filters: PartnerFilters = {}): Promise<PartnerListResponse> {
  const params = new URLSearchParams();

  if (filters.type) params.append('type', filters.type);
  if (filters.status) params.append('status', filters.status);
  if (filters.search) params.append('search', filters.search);
  // M-018 fix: Use !== undefined to avoid falsy-zero bug (page=0 would be dropped)
  if (filters.page !== undefined) params.append('page', filters.page.toString());
  if (filters.pageSize !== undefined) params.append('pageSize', filters.pageSize.toString());
  // PV-bug-fix: renter-default-view — szerver-oldali szűrés >0 bérléssel rendelkező partnerekre
  if (filters.hasRentals != null) params.append('hasRentals', String(filters.hasRentals));

  const queryString = params.toString();
  const endpoint = queryString ? `/partners-direct?${queryString}` : '/partners-direct';

  return api.get<PartnerListResponse>(endpoint);
}

/**
 * Get single partner by ID
 */
export async function getPartnerById(id: string): Promise<PartnerResponse> {
  return api.get<PartnerResponse>(`/partners-direct/${id}`);
}

/**
 * Get partner statistics
 */
export async function getPartnerStats(): Promise<{ data: PartnerStats }> {
  return api.get<{ data: PartnerStats }>('/partners-direct/stats');
}

/**
 * Create new partner
 */
export async function createPartner(data: {
  type: 'INDIVIDUAL' | 'COMPANY';
  name: string;
  companyName?: string;
  // Q-T137 #1: partner-kategóriák — a #957 óta a BE create/update perzisztálja a
  // body.categories-t (a type-infer helyett); a FE a form-értéket küldi.
  categories?: PartnerCategory[];
  contactName?: string;
  email?: string;
  phone?: string;
  taxNumber?: string;
  country?: string;
  postalCode?: string;
  city?: string;
  address?: string;
  // Átvevő (magánszemély) ID-adatok — #188 óta a BE create-path elfogadja + maszkolva tárolja
  // az idCardNumber-t (last-4, GDPR 6(1)(b) / NAIH); a többi nyers (szerződés-szükséges).
  mothersName?: string;
  birthDate?: string;
  birthPlace?: string;
  idCardNumber?: string;
  idCardExpiry?: string;
  paymentTermDays?: number;
  creditLimit?: number;
  // Q-T031 create-pár: a kedvezmény-mezők eddig hiányoztak a create-payloadból → a FE-form
  // értéke némán elveszett (a BE create-handler most fogadja, partner-direct.controller.ts).
  defaultDiscountPc?: number;
  discountRental?: number;
  discountSales?: number;
  discountService?: number;
  notes?: string;
}): Promise<PartnerResponse> {
  return api.post<PartnerResponse>('/partners-direct', data);
}

/**
 * Update partner
 */
export async function updatePartner(
  id: string,
  data: {
    type?: 'INDIVIDUAL' | 'COMPANY';
    status?: 'ACTIVE' | 'INACTIVE' | 'BLACKLISTED';
    name?: string;
    companyName?: string;
    contactName?: string;
    email?: string;
    phone?: string;
    taxNumber?: string;
    country?: string;
    postalCode?: string;
    city?: string;
    address?: string;
    // Q-T031 benign-5 + defaultDiscountPc — a BE update-handler (#198/#197) mappeli
    // (partner-direct.controller.ts). A notification/billing email .nullable() → string | null.
    notificationEmail?: string | null;
    billingEmail?: string | null;
    temporaryAddress?: string;
    tempAddressValidUntil?: string;
    lastDocumentScanDate?: string;
    defaultDiscountPc?: number;
    // Átvevő (magánszemély) ID-adatok — a BE #187 update-path elfogadja; az idCardNumber-t
    // #188 maszkolva (last-4) tárolja, a többi nyers (szerződés-szükséges, GDPR 6(1)(b)).
    // ⚠️ Conditional-omit a hívónál: üres mezőt KIHAGYNI, NEM `''`-t küldeni (a BE üres-stringre TÖRLI).
    mothersName?: string;
    birthDate?: string;
    birthPlace?: string;
    idCardNumber?: string;
    idCardExpiry?: string;
    // #196 (Q-T030) okmány-verify — false→true a BE-n stampeli idVerifiedAt/By-t.
    idVerified?: boolean;
    paymentTermDays?: number;
    creditLimit?: number;
    discountRental?: number | null;
    discountSales?: number | null;
    discountService?: number | null;
    notes?: string;
  }
): Promise<PartnerResponse> {
  return api.patch<PartnerResponse>(`/partners-direct/${id}`, data);
}

/**
 * Delete partner (soft delete)
 */
export async function deletePartner(id: string): Promise<{ success: boolean }> {
  return api.delete<{ success: boolean }>(`/partners-direct/${id}`);
}

/**
 * Update partner status (E-55 Story 55-2)
 */
export async function updatePartnerStatus(
  id: string,
  data: { status: 'ACTIVE' | 'INACTIVE' | 'TRUSTED' | 'WARNING'; warningNote?: string }
): Promise<PartnerResponse> {
  return api.patch<PartnerResponse>(`/partners-direct/${id}/status`, data);
}

/**
 * Set cost bearer for partner (E-55 Story 55-3)
 */
export async function setCostBearer(
  id: string,
  costBearerId: string | null
): Promise<PartnerResponse> {
  return api.patch<PartnerResponse>(`/partners-direct/${id}/cost-bearer`, { costBearerId });
}

/**
 * Set deposit exemption for partner (Story 78-5, Q-T031).
 * Dedikált endpoint a `@Roles('BOLTVEZETO')` RBAC-guard miatt — a sima `@Patch(:id)`
 * NEM fogadja a depositExemption-t (kaució-mentesség = pénz-kockázat → role-gated).
 * A BE a végső autoritatív gate (403 ha nem jogosult); a FE role-check csak UX.
 */
export async function updateDepositExemption(
  id: string,
  depositExemption: 'REQUIRED' | 'EXEMPT' | 'AUTOMATIC'
): Promise<PartnerResponse> {
  return api.patch<PartnerResponse>(`/partners-direct/${id}/deposit-exemption`, {
    depositExemption,
  });
}

/**
 * Get partners that have this partner as cost bearer (E-55 Story 55-3)
 */
export async function getCostBearerClients(id: string): Promise<{ data: Partner[] }> {
  return api.get<{ data: Partner[] }>(`/partners-direct/${id}/cost-bearer-clients`);
}

/**
 * Quick create partner — minimal fields, no special permissions (ADR-089)
 */
export async function quickCreatePartner(data: {
  type: 'INDIVIDUAL' | 'COMPANY';
  name: string;
  taxNumber?: string;
  email?: string;
  phone?: string;
  city?: string;
  /** Story 70-1E-a: optional OCR-prefilled id fields (ignored if backend rejects). */
  idCardNumber?: string;
  idCardExpiry?: string;
}): Promise<PartnerResponse> {
  return api.post<PartnerResponse>('/partners-direct/quick', data);
}

/**
 * Check for duplicate partners by name or taxNumber
 */
export async function checkPartnerDuplicate(params: {
  name?: string;
  taxNumber?: string;
}): Promise<{ data: Array<{ id: string; name: string; taxNumber?: string; type: string }> }> {
  const searchParams = new URLSearchParams();
  if (params.name) searchParams.append('name', params.name);
  if (params.taxNumber) searchParams.append('taxNumber', params.taxNumber);
  const queryString = searchParams.toString();
  return api.get(`/partners-direct/check-duplicate${queryString ? `?${queryString}` : ''}`);
}

// ============================================
// E-104 — Customer 360° Activity Timeline
// ============================================

export const ACTIVITY_TYPES = [
  'TASK',
  'INVOICE',
  'QUOTE',
  'DELIVERY_NOTE',
  'RENTAL',
  'WORKSHEET',
  'LOYALTY',
  'MESSAGE',
] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

export interface ActivityItem {
  type: ActivityType;
  id: string;
  occurredAt: string;
  title: string;
  summary?: string;
  amount?: number;
  status?: string;
  subtype?: string;
  metadata?: Record<string, unknown>;
}

export interface ActivityResponse {
  items: ActivityItem[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface ActivityQuery {
  cursor?: string;
  limit?: number;
  types?: ActivityType[];
}

/**
 * Get partner activity timeline — merged events from 7 sources (E-104)
 */
export async function getPartnerActivity(
  partnerId: string,
  query: ActivityQuery = {}
): Promise<{ data: ActivityResponse }> {
  const params = new URLSearchParams();
  if (query.cursor) params.append('cursor', query.cursor);
  if (query.limit !== undefined) params.append('limit', query.limit.toString());
  if (query.types) {
    for (const t of query.types) params.append('types', t);
  }
  const qs = params.toString();
  const endpoint = `/partners/${partnerId}/activity${qs ? `?${qs}` : ''}`;
  return api.get<{ data: ActivityResponse }>(endpoint);
}

/**
 * Inline quick-add NOTE or CALL to partner timeline (E-104)
 */
export async function createPartnerNote(
  partnerId: string,
  data: { title: string; description?: string; type?: 'NOTE' | 'CALL'; locationId?: string }
): Promise<{ data: ActivityItem }> {
  return api.post<{ data: ActivityItem }>(`/partners/${partnerId}/notes`, data);
}

// ============================================
// M2 — PartnerPhone CRUD
// ============================================

export interface PartnerPhone {
  id: string;
  label: string;
  number: string;
  isPrimary: boolean;
}

/**
 * Add a phone number to a partner (M2)
 */
export async function addPartnerPhone(
  partnerId: string,
  data: { label: string; number: string; isPrimary?: boolean | undefined }
): Promise<{ data: PartnerPhone }> {
  return api.post<{ data: PartnerPhone }>(`/partners-direct/${partnerId}/phones`, data);
}

/**
 * Update a phone number of a partner (M2)
 */
export async function updatePartnerPhone(
  partnerId: string,
  phoneId: string,
  data: { label?: string | undefined; number?: string | undefined; isPrimary?: boolean | undefined }
): Promise<{ data: PartnerPhone }> {
  return api.patch<{ data: PartnerPhone }>(`/partners-direct/${partnerId}/phones/${phoneId}`, data);
}

/**
 * Delete a phone number from a partner (M2)
 */
export async function deletePartnerPhone(
  partnerId: string,
  phoneId: string
): Promise<{ data: { success: boolean } }> {
  return api.delete<{ data: { success: boolean } }>(
    `/partners-direct/${partnerId}/phones/${phoneId}`
  );
}

/**
 * page9 (#420) — ContactPerson read-seam: a kontakt-lista a dedikált endpointról
 * jön, a pinnelt Zod-kontrakton runtime-parse-olva (drift → azonnali hiba).
 * Az isPrimary-first rendezés BE-garancia.
 */
export type ContactPersonRead = ContactPersonListResponseDto['data'][number];

export async function getContactPersons(partnerId: string): Promise<ContactPersonRead[]> {
  const raw = await api.get<unknown>(`/partners-direct/${partnerId}/contact-persons`);
  return ContactPersonListResponseSchema.parse(raw).data;
}

/**
 * L1(b) Partner-46 — költségviselő-pool read-seam (#822 `GET /partners-direct/:partnerId/cost-bearers`).
 * A pool CSAK UX-forrás (a pénz-cél a `Rental.costBearerId` explicit kijelölése). A UX-kritikus
 * mezők pinneltek (drift → azonnali Zod-hiba); a `createdAt` metaadat opcionális (robusztus a
 * BE-serializáció-változásra). C4: a BE csak aktív kapcsolt partnert ad vissza.
 */
const CostBearerPoolEntrySchema = z.object({
  id: z.string(),
  relatedPartnerId: z.string(),
  relatedPartnerName: z.string(),
  type: z.literal('COST_BEARER'),
  createdAt: z.string().optional(),
});
const CostBearerPoolResponseSchema = z.object({ data: z.array(CostBearerPoolEntrySchema) });
export type CostBearerPoolEntry = z.infer<typeof CostBearerPoolEntrySchema>;

export async function getCostBearers(partnerId: string): Promise<CostBearerPoolEntry[]> {
  const raw = await api.get<unknown>(`/partners-direct/${partnerId}/cost-bearers`);
  return CostBearerPoolResponseSchema.parse(raw).data;
}

// ============================================
// Q-PR4a — irányítószám → település lookup
// ============================================

/**
 * A wire-kontraktus MÉRVE dev4-en (2026-07-27), nem kód-olvasatból:
 *   GET /partners-direct/irsz/6000 → 200 {"data":{"postalCode":"6000","settlements":["Kecskemét"]}}
 *   GET /partners-direct/irsz/2066 → 200 {"data":{"postalCode":"2066","settlements":["Szár","Újbarok"]}}
 *   GET /partners-direct/irsz/9999 → 200 {"data":{"postalCode":"9999","settlements":[]}}   ← ismeretlen: 200+ÜRES
 *   GET /partners-direct/irsz/abc  → 200 {"data":{"postalCode":"abc","settlements":[]}}    ← nem-numerikus: ugyanaz
 *
 * ⚠️ Két dolog, ami a shape-feltevés helyett a MÉRÉSBŐL jön (a #981 create-burok tanulsága):
 *  1. a válasz `{ data: … }` BUROKBAN érkezik → a zod-séma a burokra megy
 *  2. ismeretlen/érvénytelen kód NEM 404, hanem 200 + üres tömb → a hívónak
 *     üres-ág kell, NEM hibaág (a blur félgépelt kódra nem dobhat hibát)
 */
const PostalCodeLookupSchema = z.object({
  data: z.object({
    postalCode: z.string(),
    settlements: z.array(z.string()),
  }),
});

export type PostalCodeLookup = z.infer<typeof PostalCodeLookupSchema>['data'];

/** Nemzeti referencia-adat (nem tenant-scoped). Guard: csak JwtAuthGuard → minden szerep hívhatja. */
export async function lookupPostalCode(code: string): Promise<PostalCodeLookup> {
  const raw = await api.get<unknown>(`/partners-direct/irsz/${encodeURIComponent(code)}`);
  return PostalCodeLookupSchema.parse(raw).data;
}
